import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./PaymentReturn.css";
import { supabase } from "../../supabase";
import { captureVippsPayment, completeCheckout, getPaymentDetails } from "../payments/vipps";
import { sendReceiptEmail } from "../../utils/sendReceiptEmail";
import { sendReceiptSms } from "../../utils/sendReceiptSms";
import { pointsForPurchase } from "./cartPricing";
import { useParkSettings } from "../../utils/parkSettings";

export default function PaymentReturn() {
  const { t } = useTranslation('paymentReturn');
  const [orderReference, setOrderReference] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [buyerName, setBuyerName] = useState("");
  const [email, setEmail] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [discountAmount, setDiscountAmount] = useState(0);
  const [cartItems, setCartItems] = useState([]);
  const [products] = useState([]);
  const [pspReference, setPspReference] = useState("");
  // null = checking, 'SUCCESS', 'CANCELLED', 'EXPIRED', 'FAILED'
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [guestTicketCount, setGuestTicketCount] = useState(null);
  // Guest quick checkout only has a phone number at this point - name/email
  // come back from an extra Vipps round-trip in guest-checkout/complete, which
  // can take a beat. Members already have both from localStorage, so nothing
  // to wait on for them.
  const [guestInfoLoading, setGuestInfoLoading] = useState(localStorage.getItem('isGuestCheckout') === '1');
  // Read once - the flag is removed from localStorage when the order completes.
  // Guests earn no points, so they don't get the points banner.
  const [wasGuestCheckout] = useState(() => localStorage.getItem('isGuestCheckout') === '1');
  // Points the server actually gave (its percentage counts, not the app's estimate)
  const [serverPoints, setServerPoints] = useState(null);
  const { pointsPercent } = useParkSettings();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const rawTotal = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );
  const totalPrice = Math.max(0, rawTotal - discountAmount);

  // Load order info from localStorage on mount
  useEffect(() => {
    // Try to get reference from URL first, then localStorage
    const urlReference = searchParams.get('reference') || searchParams.get('ref');
    const storedReference = localStorage.getItem("orderReference");
    const finalReference = urlReference || storedReference;

    const storedPhone = localStorage.getItem("phoneNumber");
    const storedName = localStorage.getItem("buyerName");
    const storedEmail = localStorage.getItem("email");
    const storedCart = localStorage.getItem("cartItems");

    if (finalReference) setOrderReference(finalReference);
    if (storedPhone) setPhoneNumber(storedPhone);
    if (storedName) setBuyerName(storedName);
    if (storedEmail) setEmail(storedEmail);
    const storedDiscountCode = localStorage.getItem("discountCode") || "";
    const storedDiscountAmount = parseFloat(localStorage.getItem("discountAmount") || "0") || 0;
    if (storedDiscountCode) setDiscountCode(storedDiscountCode);
    if (storedDiscountAmount > 0) setDiscountAmount(storedDiscountAmount);

    if (storedCart) {
      const parsedCart = JSON.parse(storedCart);
      setCartItems(parsedCart);

      const now = new Date().toISOString();

      // Update cart items with orderReference and datePurchased
      const updatedCart = parsedCart.map(item => {
        let newItem = { ...item, orderReference: finalReference, datePurchased: now };
        // Ensure klippekort has correct stampAmounts and stampTotal
        if (item.category === "klippekort" && item.type === "stampCardTicket") {
          // Last resort: parse number from product name (e.g. "Klippekort 30" → 30)
          const nameMatch = (item.name || "").match(/\b(\d+)\b/);
          const nameStamps = nameMatch ? parseInt(nameMatch[1], 10) : null;
          const resolved = item.stampAmounts ?? item.stampTotal ?? nameStamps ?? item.quantity ?? 0;
          newItem.stampAmounts = resolved;
          newItem.stampTotal = resolved;
        }
        return newItem;
      });
      localStorage.setItem("cartItems", JSON.stringify(updatedCart));

      // Store order in localStorage orders array (for offline use)
      const storedOrders = JSON.parse(localStorage.getItem("orders") || "[]");
      const alreadyExists = storedOrders.some(
        o => o.orderReference === finalReference
      );
      if (!alreadyExists) {
        const newOrder = {
          orderReference: finalReference || "",
          buyerName: storedName || "",
          phoneNumber: storedPhone || "",
          email: storedEmail || "",
          datePurchased: now,
          items: updatedCart,
          totalPrice: updatedCart.reduce(
            (sum, item) => sum + item.price * item.quantity,
            0
          ),
        };
        localStorage.setItem("orders", JSON.stringify([newOrder, ...storedOrders]));
      }
    }
  }, [searchParams]);

  // Check Vipps payment status once orderReference is loaded
  useEffect(() => {
    if (!orderReference) return;

    getPaymentDetails(orderReference)
      .then((data) => {
        const state = data?.state;

        if (state === 'AUTHORIZED' || state === 'CAPTURED' || state === 'PARTIALLY_CAPTURED') {
          setPaymentStatus('SUCCESS');
          setPspReference(orderReference);
        } else if (state === 'ABORTED' || state === 'TERMINATED') {
          setPaymentStatus('CANCELLED');
          // Tickets.jsx redirects back here forever as long as this stays set
          // (its own recovery check for a payment that skipped PaymentReturn
          // entirely) - the success path clears it further down, but a failed/
          // cancelled/expired payment never reaches that code, so it has to
          // clear it here instead.
          localStorage.removeItem("orderReference");
          localStorage.removeItem("orderReferenceSetAt");
        } else if (state === 'EXPIRED') {
          setPaymentStatus('EXPIRED');
          localStorage.removeItem("orderReference");
          localStorage.removeItem("orderReferenceSetAt");
        } else if (state === 'UNKNOWN') {
          // Payment details couldn't be fetched - assume success rather than
          // stranding a customer who did pay on a false failure screen.
          setPaymentStatus('SUCCESS');
          setPspReference(orderReference);
        } else {
          setPaymentStatus('FAILED');
          localStorage.removeItem("orderReference");
          localStorage.removeItem("orderReferenceSetAt");
        }
      })
      .catch((err) => {
        console.error('Unexpected error in getPaymentDetails:', err);
        // Same reasoning as the UNKNOWN case above.
        setPaymentStatus('SUCCESS');
        setPspReference(orderReference);
      });
  }, [orderReference]);

  // Save order to Supabase after pspReference is set
  useEffect(() => {
    if (!pspReference) return;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      const user = session?.user;
      const storedReference = localStorage.getItem("orderReference");
      const storedPhone = localStorage.getItem("phoneNumber");
      const storedName = localStorage.getItem("buyerName");
      const storedEmail = localStorage.getItem("email");
      const storedCart = localStorage.getItem("cartItems");

      if (!user) {
        const wasGuestCheckout = localStorage.getItem('isGuestCheckout') === '1';
        if (!wasGuestCheckout) {
          // Checkout requires login (MyCart.jsx only shows the pay button once
          // logged in) unless it was a guest checkout, so this means the session
          // expired between paying and landing back here - rare, but the payment
          // still went through and there's no account left to attach the order to.
          console.error('Payment succeeded but no logged-in user on return - order not created.', storedReference);
          return;
        }

        // Guests have no login session, so their order goes through the guest
        // endpoint (members use /api/checkout/complete below). Both are server-side.
        try {
          const completeBody = await completeCheckout(storedReference, null, '/api/guest-checkout/complete');
          setGuestTicketCount(completeBody.ticketCount);
          // Quick checkout only collected a phone number - the real name/email
          // came back from Vipps's own consent screen instead, resolved
          // server-side in guestCheckout.js and returned here for display.
          if (completeBody.buyerName) setBuyerName(completeBody.buyerName);
          if (completeBody.email) setEmail(completeBody.email);

          await fetch(`${process.env.REACT_APP_BACKEND_URL}/capture-payment`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              reference: storedReference,
              userId: (storedPhone || '').replace(/\D/g, ''),
            }),
          }).catch((captureErr) => console.error('Guest payment capture failed:', captureErr));
        } catch (guestErr) {
          console.error('Guest checkout completion exception:', guestErr);
        } finally {
          // Same leak as the logged-in path below: leaving these set sends
          // Tickets.jsx's recovery check back here on every future visit,
          // showing the payment result again forever for this guest purchase.
          localStorage.removeItem('isGuestCheckout');
          localStorage.removeItem("orderReference");
          localStorage.removeItem("orderReferenceSetAt");
          localStorage.removeItem("phoneNumber");
          localStorage.removeItem("buyerName");
          localStorage.removeItem("email");
          localStorage.removeItem("paymentMethod");
          localStorage.removeItem("cartItems");
          localStorage.removeItem("cartTimestamp");
          localStorage.removeItem("productQuantities");
          localStorage.removeItem("discountId");
          localStorage.removeItem("discountCode");
          localStorage.removeItem("discountAmount");
          setGuestInfoLoading(false);
        }
        return;
      }

      if (storedReference) {
        const parsedCart = storedCart ? JSON.parse(storedCart) : [];
        const now = new Date().toISOString();
        try {
          // The backend checks the payment with Vipps and writes the order,
          // tickets, klippekort, points and discount use - the app never writes
          // those rows itself (anyone could, from the browser console).
          const result = await completeCheckout(storedReference, session.access_token);
          if (typeof result.pointsEarned === 'number') setServerPoints(result.pointsEarned);

          if (!result.alreadyCompleted) {
            // The backend captures the amount it recorded at create-payment.
            const captureResult = await captureVippsPayment({
              reference: storedReference,
              amountValue: Math.round(result.totalPrice * 100),
            });

            if (storedEmail) {
              try {
                const emailResult = await sendReceiptEmail(storedEmail, {
                  orderNumber: storedReference,
                  purchaseDate: now,
                  totalAmount: result.totalPrice,
                  items: parsedCart,
                  buyerName: storedName,
                  phoneNumber: storedPhone,
                  email: storedEmail,
                  pspReference: captureResult.pspReference || storedReference,
                  paymentMethod: "Vipps"
                });
                if (!emailResult.success) console.error('Failed to send receipt email:', emailResult.error);
              } catch (emailError) {
                console.error('Error sending receipt email:', emailError);
              }
            }

            // One SMS per order (not per email) - has its own phone-number check
            // since a customer can give an email without a phone or vice versa.
            if (storedPhone) {
              try {
                const smsResult = await sendReceiptSms(storedPhone, storedReference);
                if (!smsResult.success) console.error('Failed to send receipt SMS:', smsResult.error);
              } catch (smsError) {
                console.error('Error sending receipt SMS:', smsError);
              }
            }
          }
        } catch (err) {
          console.error("Order completion failed:", err);
        } finally {
          // Clear PII, discount data, and the cart from localStorage after order is processed
          for (const key of ["email", "phoneNumber", "buyerName", "paymentMethod", "orderReference", "orderReferenceSetAt",
            "cartItems", "cartTimestamp", "productQuantities", "discountId", "discountCode", "discountAmount"]) {
            localStorage.removeItem(key);
          }
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [pspReference]);

  // Only show klippekort as a single item, do not split into 1-hour tickets
  const tickets = cartItems
    .filter((item) =>
      (item.category === "lek" && item.type === "ticket") ||
      item.category === "golf" ||
      (item.category === "klippekort" && item.type === "stampCardTicket")
    )
    .map((item) => {
      const product = products.find((p) => p.id === item.productId);
      const baseDuration = product?.duration ?? item.duration ?? 0;
      const bonus = (typeof item.bonusMinutes === 'number' ? item.bonusMinutes : parseInt(item.bonusMinutes, 10)) ||
                    (typeof item.bonusTime === 'number' ? item.bonusTime : parseInt(item.bonusTime, 10)) || 0;
        const nameMatch2 = (item.name || "").match(/\b(\d+)\b/);
        const nameStamps2 = nameMatch2 ? parseInt(nameMatch2[1], 10) : null;
        return {
          ...item,
          duration: (typeof baseDuration === 'number' ? baseDuration : parseInt(baseDuration, 10) || 0) + (Number.isFinite(bonus) ? bonus : 0),
          stampAmounts: item.stampAmounts ?? item.stampTotal ?? nameStamps2 ?? item.quantity ?? 0
        };
    });

  // --- Cancelled state ---
  if (paymentStatus === 'CANCELLED') {
    return (
      <div className="pr-page">
        <div className="pr-hero pr-hero--fail">
          <div className="pr-status-icon pr-status-icon--fail">✕</div>
          <h2>{t('cancelledTitle')}</h2>
          <p>{t('cancelledDesc')}</p>
        </div>
        <div className="pr-card">
          <button className="pr-btn-primary" onClick={() => navigate('/MyCart')}>{t('tryAgain')}</button>
          <button className="pr-btn-ghost" onClick={() => navigate('/')}>{t('goToFrontpage')}</button>
        </div>
      </div>
    );
  }

  // --- Failed / expired state ---
  if (paymentStatus === 'FAILED' || paymentStatus === 'EXPIRED') {
    const isExpired = paymentStatus === 'EXPIRED';
    return (
      <div className="pr-page">
        <div className="pr-hero pr-hero--fail">
          <div className="pr-status-icon pr-status-icon--fail">!</div>
          <h2>{isExpired ? t('expiredTitle') : t('failedTitle')}</h2>
          <p>{isExpired ? t('expiredDesc') : t('failedDesc')}</p>
        </div>
        <div className="pr-card">
          <button className="pr-btn-primary" onClick={() => navigate('/MyCart')}>{t('tryAgain')}</button>
          <button className="pr-btn-ghost" onClick={() => navigate('/')}>{t('goToFrontpage')}</button>
        </div>
      </div>
    );
  }

  // --- Loading / checking status ---
  if (paymentStatus === null) {
    return (
      <div className="pr-page">
        <div className="pr-hero">
          <div className="pr-spinner" />
          <p className="pr-loading-text">{t('checkingStatus')}</p>
        </div>
      </div>
    );
  }

  const purchaseDate = (() => {
    // This is the order-confirmation screen - cartItems is just the pre-checkout
    // localStorage cart, which never carries a purchase timestamp, so "now" is
    // the actual purchase moment rather than a field to read back from anywhere.
    const d = new Date();
    const dd = String(d.getDate()).padStart(2,'0');
    const mm = String(d.getMonth()+1).padStart(2,'0');
    const yy = String(d.getFullYear()).slice(-2);
    const hh = String(d.getHours()).padStart(2,'0');
    const min = String(d.getMinutes()).padStart(2,'0');
    return `${dd}.${mm}.${yy} kl. ${hh}:${min}`;
  })();

  const hasTickets = tickets.length > 0;

  return (
    <div className="pr-page">

      {/* Success hero */}
      <div className="pr-hero">
        <div className="pr-status-icon pr-status-icon--success">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" width="32" height="32">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </div>
        <h2>{t('thankYouTitle')}</h2>
        <p>
          {guestTicketCount !== null
            ? t('confirmedGuest', {
                ticketPhrase: t(guestTicketCount > 1 ? 'ticketPhrasePlural' : 'ticketPhraseSingular'),
                email: email || t('yourEmail'),
              })
            : t('confirmedMember')}
        </p>
        <div className="pr-order-ref">
          <span className="pr-order-ref-label">{t('order')}</span>
          <span className="pr-order-ref-num">OR-{orderReference ? orderReference.substring(0, 8) : 'N/A'}</span>
        </div>
      </div>

      {/* Kundeinfo */}
      <div className="pr-section-label">{t('customerInfo')}</div>
      <div className="pr-card">
        <div className="pr-info-row">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          <span>{t('nameLabel')}</span>
          {guestInfoLoading
            ? <span className="pr-skeleton-bar" />
            : <strong>{buyerName || 'N/A'}</strong>}
        </div>
        <div className="pr-divider" />
        <div className="pr-info-row">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
          <span>{t('emailLabel')}</span>
          {guestInfoLoading
            ? <span className="pr-skeleton-bar" />
            : <strong>{email || 'N/A'}</strong>}
        </div>
        <div className="pr-divider" />
        <div className="pr-info-row">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.4 2 2 0 0 1 3.6 1.22h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.82a16 16 0 0 0 6.29 6.29l.95-.94a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
          <span>{t('phoneLabel')}</span><strong>{phoneNumber || 'N/A'}</strong>
        </div>
        <div className="pr-divider" />
        <div className="pr-info-row">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          <span>{t('purchaseDateLabel')}</span><strong>{purchaseDate}</strong>
        </div>
      </div>

      {/* Ordre sammendrag */}
      <div className="pr-section-label">{t('orderSummary')}</div>
      <div className="pr-card">
        {cartItems.map((item, idx) => (
          <div key={item.productId || idx} className="pr-order-row">
            <div className="pr-order-row-left">
              <div className="pr-qty-badge">{item.quantity}</div>
              <span>{item.name}</span>
            </div>
            <strong>{item.price * item.quantity} kr</strong>
          </div>
        ))}
        {discountAmount > 0 && (
          <div className="pr-discount-row">
            <span>{t('discountCode', { code: discountCode })}</span>
            <strong>-{discountAmount} kr</strong>
          </div>
        )}
        <p className="pr-mva-note">{t('mvaNote')}</p>
        <div className="pr-total-row">
          <span className="pr-total-label">{t('totalsum')}</span>
          <span className="pr-total-amount">{totalPrice} kr</span>
        </div>
        {!wasGuestCheckout && <div className="pr-points-banner">
          <div className="pr-points-top">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
            {t('youEarnedPoints', { points: (serverPoints ?? pointsForPurchase(totalPrice, pointsPercent)).toFixed(2) })}
          </div>
          <p>{t('pointsAddedToProfile')}</p>
        </div>}
      </div>

      {/* Actions */}
      <div className="pr-section-label">{t('whatNow')}</div>
      <div className="pr-card">
        {/* A guest has no account to view tickets/klippekort in - their QR
            already went to their email, these buttons would just be empty/broken. */}
        {guestTicketCount === null && (
          <>
            <button className="pr-btn-primary" onClick={() => navigate('/tickets')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="17" height="17"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/><line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/></svg>
              {t('viewMyTickets')}
            </button>
            {hasTickets && (
              <button className="pr-btn-green" onClick={() => navigate('/klippekort-redeem')}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="17" height="17"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                {t('redeemKlippekort')}
              </button>
            )}
          </>
        )}
        <button className="pr-btn-ghost" onClick={() => navigate('/')}>{t('goToFrontpage')}</button>
      </div>
    </div>
  );
}
